import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { seedSampleTrip, listSeededPresets } from "@/lib/onboarding/seed";
import {
  SAMPLE_PRESETS_LIST,
  type SamplePresetKey,
} from "@/lib/onboarding/sampleTrips";
import { ACTIVE_TRIP_EVENT } from "@/lib/active-trip";

type Props = {
  /** Called when the user taps "No thanks, finish setup". */
  onFinish: () => void;
};

/**
 * Card grid shown at the end of the onboarding tour. Lets the user seed
 * any combination of the four sample trip presets before finishing.
 */
export function SampleTripPicker({ onFinish }: Props) {
  const [added, setAdded] = useState<Set<SamplePresetKey>>(new Set());
  const [busy, setBusy] = useState<SamplePresetKey | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const qc = useQueryClient();

  // Pre-fill state if the user already seeded some presets in a prior session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const set = await listSeededPresets();
      if (!cancelled) {
        setAdded(set);
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdd = async (key: SamplePresetKey, name: string) => {
    if (added.has(key) || busy) return;
    setBusy(key);
    try {
      await seedSampleTrip(key);
      setAdded((prev) => new Set(prev).add(key));
      toast.success(`${name} added to your trips`);
      qc.invalidateQueries({ queryKey: ["my-trips-list"] });
      qc.invalidateQueries({ queryKey: ["my-trip"] });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(ACTIVE_TRIP_EVENT, { detail: {} }));
      }
    } catch (e) {
      toast.error("Couldn't add sample trip", {
        description: e instanceof Error ? e.message : "Try again from Settings → Account.",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3">
      <div className="grid grid-cols-2 gap-3">
        {SAMPLE_PRESETS_LIST.map((p) => {
          const isAdded = added.has(p.key);
          const isBusy = busy === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => { void handleAdd(p.key, p.shortName); }}
              disabled={isAdded || isBusy || !hydrated}
              aria-pressed={isAdded}
              className={cn(
                "group relative text-left rounded-2xl overflow-hidden",
                "border border-[var(--roava-border)] bg-[var(--roava-surface)]",
                "transition active:scale-[0.98] disabled:active:scale-100",
                isAdded
                  ? "ring-2 ring-[var(--roava-green)] ring-offset-1 ring-offset-[var(--roava-bg)]"
                  : "hover:border-[var(--roava-blue)]/50",
              )}
            >
              <div className="relative aspect-[4/3] bg-[var(--roava-border)]/30">
                <img
                  src={p.heroUrl}
                  alt=""
                  loading="lazy"
                  className={cn(
                    "absolute inset-0 size-full object-cover",
                    isAdded && "opacity-90",
                  )}
                />
                {isAdded && (
                  <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-[var(--roava-green)] text-white text-[11px] font-semibold px-2 py-0.5 shadow">
                    <Check className="size-3" /> Added
                  </div>
                )}
                {isBusy && (
                  <div className="absolute inset-0 grid place-items-center bg-black/30">
                    <Loader2 className="size-5 text-white animate-spin" />
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-[13px] font-semibold text-[var(--roava-text)] leading-tight">
                  {p.shortName}
                </div>
                <div className="mt-0.5 text-[11px] text-[var(--roava-text-muted)] leading-snug line-clamp-2">
                  {p.subtitle}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onFinish}
        className="mt-4 w-full h-11 rounded-2xl text-sm font-semibold text-[var(--roava-text)] border border-[var(--roava-border)] hover:bg-[var(--roava-surface)] inline-flex items-center justify-center gap-2"
      >
        {added.size > 0 ? (
          <>
            <Sparkles className="size-4 text-[var(--roava-blue)]" />
            Finish setup ({added.size} added)
          </>
        ) : (
          "No thanks, finish setup"
        )}
      </button>
    </div>
  );
}
